import { Contract, Interface, Signer } from "ethers";
import { useState, useEffect } from "react";
import useSWR from "swr";
import { getProvider } from "lib/rpc";

import { getContract } from "config/contracts";
import { BASIS_POINTS_DIVISOR_BIGINT } from "config/factors";
import { getV1Tokens, getWhitelistedV1Tokens } from "config/tokens";
import { bigMath } from "lib/bigmath";
import { contractFetcher } from "lib/contracts";
import { DEFAULT_MAX_USDG_AMOUNT, MAX_PRICE_DEVIATION_BASIS_POINTS, USDG_ADDRESS } from "lib/legacy";
import { USD_DECIMALS } from "config/factors";
import { expandDecimals } from "lib/numbers";
import { BESC } from "config/static/chains";

import { InfoTokens, Token, TokenInfo } from "./types";
import { getSpread } from "./utils";
import { useLiveIndexPrices } from "lib/indexer/useLivePrices";

import VaultReader from "sdk/abis/VaultReader.json";

// Minimal ABI fragments for direct storage reads — these never touch the price feed.
const VAULT_OI_ABI = [
  "function poolAmounts(address) external view returns (uint256)",
  "function reservedAmounts(address) external view returns (uint256)",
  "function guaranteedUsd(address) external view returns (uint256)",
  "function globalShortSizes(address) external view returns (uint256)",
  // Synthetic long OI is tracked separately (keyed by index token), because for
  // synthetic longs guaranteedUsd is keyed by the BUSDC collateral, not the index.
  "function globalSyntheticLongSizes(address) external view returns (uint256)",
];
const PM_OI_ABI = [
  "function maxGlobalShortSizes(address) external view returns (uint256)",
  "function maxGlobalLongSizes(address) external view returns (uint256)",
];

// Multicall3 (same address on BESC) — batches all synthetic reads into ONE
// eth_call instead of ~240 individual requests, which otherwise flood the RPC.
const MULTICALL3_ADDRESS = "0x6dE3F6ae5a7Ba67Ec2cD12703a6Ce6F10Ca5EfEa";
const MULTICALL3_ABI = [
  "function aggregate3((address target, bool allowFailure, bytes callData)[] calls) view returns ((bool success, bytes returnData)[] returnData)",
];
const VAULT_IFACE = new Interface([
  ...VAULT_OI_ABI,
  "function getNextFundingRate(address) view returns (uint256)",
  "function cumulativeFundingRates(address) view returns (uint256)",
  "function getMinPrice(address) view returns (uint256)",
]);
// The MLP pool's real backing tokens on BESC (its "composition"). Synthetic
// long/short liquidity is validated against the sum of ALL of these, not just
// the BUSDC stable leg — the whole MLP is the counterparty.
const POOL_TOKENS = [
  "0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B", // BUSDC
  "0x33e22F85CC1877697773ca5c85988663388883A0", // WBESC
  "0xf794E0B0c0a91099ccAcac296193e04Fb860442a", // MONEY
];
const PM_IFACE = new Interface(PM_OI_ABI);
// decode a uint256 aggregate3 Result (bytes32) → bigint; 0n on failure/empty.
function decodeUint(r: any): bigint {
  const ok = r?.success ?? r?.[0];
  const data = r?.returnData ?? r?.[1];
  if (!ok || !data || data === "0x") return 0n;
  try {
    return BigInt(data);
  } catch (_) {
    return 0n;
  }
}

export interface SynthOI {
  poolAmount: bigint;
  reservedAmount: bigint;
  guaranteedUsd: bigint;
  globalShortSize: bigint;
  globalLongSize: bigint;
  maxGlobalShortSize: bigint;
  maxGlobalLongSize: bigint;
}

export function useInfoTokens(
  signer: Signer | undefined,
  chainId: number,
  active: boolean,
  tokenBalances?: bigint[],
  fundingRateInfo?: bigint[],
  vaultPropsLength?: number
) {
  const tokens = getV1Tokens(chainId);
  const vaultReaderAddress = getContract(chainId, "VaultReader");
  // BESC: read pool/token info from the LIVE SynthVault, not the retired main
  // vault ("Vault") whose poolAmounts are stale (phantom BUSDC, $0 MONEY/WBESC).
  // The BESC whitelist is only the 3 pool tokens, all of which price on
  // SynthVault, so getVaultTokenInfoV4 works there. Positions are read
  // separately (Exchange.tsx) and are intentionally left untouched.
  let vaultAddress = getContract(chainId, "Vault");
  let positionRouterAddress = getContract(chainId, "PositionRouter");
  if (chainId === BESC) {
    try {
      vaultAddress = getContract(chainId, "SynthVault" as any);
      positionRouterAddress = getContract(chainId, "SynthPositionRouter" as any);
    } catch (_) {}
  }
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");

  const whitelistedTokens = getWhitelistedV1Tokens(chainId);
  const whitelistedTokenAddresses = whitelistedTokens.map((token) => token.address);

  const { data: vaultTokenInfo } = useSWR<bigint[], any>(
    [`useInfoTokens:${active}`, chainId, vaultReaderAddress, "getVaultTokenInfoV4"],
    {
      fetcher: contractFetcher(signer, VaultReader, [
        vaultAddress,
        positionRouterAddress,
        nativeTokenAddress,
        expandDecimals(1, 18),
        whitelistedTokenAddresses,
      ]) as any,
    }
  );

  // SynthVault — BESC only
  let synthVaultAddress: string | undefined;
  let synthPositionRouterAddress: string | undefined;
  try {
    synthVaultAddress = getContract(chainId, "SynthVault" as any);
    synthPositionRouterAddress = getContract(chainId, "SynthPositionRouter" as any);
  } catch (_) {}

  const synthTokens = chainId === BESC ? getV1Tokens(chainId).filter((t) => t.isSynthetic && !t.isStable) : [];
  const synthTokenAddresses = synthTokens.map((t) => t.address);

  // Read OI data directly from SynthVault storage — bypasses VaultPriceFeed entirely.
  // getVaultTokenInfoV4 calls vault.getMinPrice() which reverts when no on-chain oracle
  // is configured for synthetic tokens. Direct storage reads never touch the price feed.
  const [synthOiData, setSynthOiData] = useState<SynthOI[] | undefined>(undefined);
  const [synthFundingRates, setSynthFundingRates] = useState<bigint[] | undefined>(undefined);
  // Synthetic markets are backed by the WHOLE MLP pool (BUSDC + WBESC + MONEY),
  // not the (zero) index-token pool. This holds the total available USD across
  // all composition tokens — the liquidity ceiling shared by every synth market.
  const [synthStablePoolUsd, setSynthStablePoolUsd] = useState<bigint>(0n);

  useEffect(() => {
    if (!synthVaultAddress || !synthPositionRouterAddress || synthTokenAddresses.length === 0) {
      return;
    }

    const providerOrSigner = signer ?? getProvider(undefined, chainId);
    const mc = new Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, providerOrSigner);
    const vault = synthVaultAddress;
    const pr = synthPositionRouterAddress;
    // 9 reads per synthetic token (OI + funding); pool-composition reads appended after.
    const PER_TOKEN = 9;

    // Fetch EVERYTHING (OI, funding, stable pool) for all synth markets in a
    // single Multicall3.aggregate3 request, instead of ~240 separate eth_calls
    // that flood the RPC and time out.
    const fetchAll = async () => {
      const calls: { target: string; allowFailure: boolean; callData: string }[] = [];
      for (const addr of synthTokenAddresses) {
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("poolAmounts", [addr]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("reservedAmounts", [addr]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("guaranteedUsd", [addr]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("globalShortSizes", [addr]) });
        calls.push({ target: pr, allowFailure: true, callData: PM_IFACE.encodeFunctionData("maxGlobalShortSizes", [addr]) });
        calls.push({ target: pr, allowFailure: true, callData: PM_IFACE.encodeFunctionData("maxGlobalLongSizes", [addr]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("getNextFundingRate", [addr]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("cumulativeFundingRates", [addr]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("globalSyntheticLongSizes", [addr]) });
      }
      // MLP composition (BUSDC + WBESC + MONEY): pool, reserved, price per token.
      // Available synthetic liquidity = Σ (pool - reserved) * price across all.
      for (const t of POOL_TOKENS) {
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("poolAmounts", [t]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("reservedAmounts", [t]) });
        calls.push({ target: vault, allowFailure: true, callData: VAULT_IFACE.encodeFunctionData("getMinPrice", [t]) });
      }

      try {
        const res: any[] = await mc.aggregate3(calls);
        const oi: SynthOI[] = [];
        const flat: bigint[] = []; // [nextRate_0, cumRate_0, nextRate_1, ...] — matches Reader.getFundingRates
        for (let i = 0; i < synthTokenAddresses.length; i++) {
          const b = i * PER_TOKEN;
          oi.push({
            poolAmount: decodeUint(res[b]),
            reservedAmount: decodeUint(res[b + 1]),
            guaranteedUsd: decodeUint(res[b + 2]),
            globalShortSize: decodeUint(res[b + 3]),
            maxGlobalShortSize: decodeUint(res[b + 4]),
            maxGlobalLongSize: decodeUint(res[b + 5]),
            globalLongSize: decodeUint(res[b + 8]),
          });
          flat.push(decodeUint(res[b + 6]), decodeUint(res[b + 7]));
        }
        setSynthOiData(oi);
        setSynthFundingRates(flat);

        // Total MLP composition available liquidity (USD, 1e30). Each pool token
        // is 18-dec and getMinPrice returns 1e30 per whole token, so per token
        // usd = (pool - reserved) * price / 1e18. Summed across BUSDC/WBESC/MONEY.
        const base = synthTokenAddresses.length * PER_TOKEN;
        let totalPoolUsd = 0n;
        for (let j = 0; j < POOL_TOKENS.length; j++) {
          const p = base + j * 3;
          const pool = decodeUint(res[p]);
          const reserved = decodeUint(res[p + 1]);
          const price = decodeUint(res[p + 2]);
          const avail = pool > reserved ? pool - reserved : 0n;
          totalPoolUsd += (avail * price) / 10n ** 18n;
        }
        setSynthStablePoolUsd(totalPoolUsd);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.warn("[SynthVault] batched read failed:", err?.message ?? err);
      }
    };

    fetchAll();
    const id = setInterval(fetchAll, 30_000);
    return () => clearInterval(id);
    // synthTokenAddresses is stable (static config for this chainId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, chainId, synthVaultAddress, synthPositionRouterAddress, signer]);

  // Live prices from Binance via WebSocket — sub-second, no polling needed
  const indexPrices = useLiveIndexPrices(chainId);

  return {
    infoTokens: getInfoTokens(
      tokens,
      tokenBalances,
      whitelistedTokens,
      vaultTokenInfo,
      fundingRateInfo,
      vaultPropsLength,
      indexPrices,
      nativeTokenAddress,
      synthTokens,
      synthOiData,
      synthFundingRates,
      synthStablePoolUsd
    ),
  };
}

function getInfoTokens(
  tokens: Token[],
  tokenBalances: bigint[] | undefined,
  whitelistedTokens: Token[],
  vaultTokenInfo: bigint[] | undefined,
  fundingRateInfo: bigint[] | undefined,
  vaultPropsLength: number | undefined,
  indexPrices: { [address: string]: bigint },
  nativeTokenAddress: string,
  synthTokens: Token[] = [],
  synthOiData: SynthOI[] | undefined = undefined,
  synthFundingRates: bigint[] | undefined = undefined,
  synthStablePoolUsd: bigint = 0n
): InfoTokens {
  if (!vaultPropsLength) {
    vaultPropsLength = 15;
  }
  const fundingRatePropsLength = 2;
  const infoTokens: InfoTokens = {};

  for (let i = 0; i < tokens.length; i++) {
    const token = JSON.parse(JSON.stringify(tokens[i])) as TokenInfo;

    if (tokenBalances) {
      token.balance = tokenBalances[i];
    }

    if (token.address.toLowerCase() === USDG_ADDRESS.toLowerCase()) {
      token.minPrice = expandDecimals(1, USD_DECIMALS);
      token.maxPrice = expandDecimals(1, USD_DECIMALS);
    }

    // Inject live prices for synthetic index tokens (no Vault price feed on BESC for these).
    if (token.isSynthetic && !token.isStable) {
      setTokenUsingIndexPrices(token, indexPrices, nativeTokenAddress);
    }

    infoTokens[token.address.toLowerCase()] = token;
  }

  for (let i = 0; i < whitelistedTokens.length; i++) {
    const token = JSON.parse(JSON.stringify(whitelistedTokens[i])) as TokenInfo;

    if (vaultTokenInfo) {
      token.poolAmount = vaultTokenInfo[i * vaultPropsLength];
      token.reservedAmount = vaultTokenInfo[i * vaultPropsLength + 1];
      token.availableAmount = token.poolAmount - token.reservedAmount;
      token.usdgAmount = vaultTokenInfo[i * vaultPropsLength + 2];
      token.redemptionAmount = vaultTokenInfo[i * vaultPropsLength + 3];
      token.weight = vaultTokenInfo[i * vaultPropsLength + 4];
      token.bufferAmount = vaultTokenInfo[i * vaultPropsLength + 5];
      token.maxUsdgAmount = vaultTokenInfo[i * vaultPropsLength + 6];
      token.globalShortSize = vaultTokenInfo[i * vaultPropsLength + 7];
      token.maxGlobalShortSize = vaultTokenInfo[i * vaultPropsLength + 8];
      token.maxGlobalLongSize = vaultTokenInfo[i * vaultPropsLength + 9];
      token.minPrice = vaultTokenInfo[i * vaultPropsLength + 10];
      token.maxPrice = vaultTokenInfo[i * vaultPropsLength + 11];
      token.spread = getSpread({ minPrice: token.minPrice, maxPrice: token.maxPrice });
      token.guaranteedUsd = vaultTokenInfo[i * vaultPropsLength + 12];
      token.maxPrimaryPrice = vaultTokenInfo[i * vaultPropsLength + 13];
      token.minPrimaryPrice = vaultTokenInfo[i * vaultPropsLength + 14];

      token.contractMinPrice = token.minPrice;
      token.contractMaxPrice = token.maxPrice;

      token.maxAvailableShort = 0n;
      token.hasMaxAvailableShort = false;
      if (token.maxGlobalShortSize > 0) {
        token.hasMaxAvailableShort = true;
        if (token.maxGlobalShortSize > token.globalShortSize) {
          token.maxAvailableShort = token.maxGlobalShortSize - token.globalShortSize;
        }
      }

      if (token.maxUsdgAmount == 0n) {
        token.maxUsdgAmount = DEFAULT_MAX_USDG_AMOUNT;
      }

      token.availableUsd = token.isStable
        ? bigMath.mulDiv(token.poolAmount, token.minPrice, expandDecimals(1, token.decimals))
        : bigMath.mulDiv(token.availableAmount, token.minPrice, expandDecimals(1, token.decimals));

      token.maxAvailableLong = 0n;
      token.hasMaxAvailableLong = false;
      if (token.maxGlobalLongSize > 0) {
        token.hasMaxAvailableLong = true;
        if (token.maxGlobalLongSize > token.guaranteedUsd) {
          const remainingLongSize = token.maxGlobalLongSize - token.guaranteedUsd;
          token.maxAvailableLong = remainingLongSize < token.availableUsd ? remainingLongSize : token.availableUsd;
        }
      } else {
        token.maxAvailableLong = token.availableUsd;
      }

      token.maxLongCapacity =
        token.maxGlobalLongSize > 0 && token.maxGlobalLongSize < token.availableUsd + token.guaranteedUsd
          ? token.maxGlobalLongSize
          : token.availableUsd + token.guaranteedUsd;

      token.managedUsd = token.availableUsd + token.guaranteedUsd;
      token.managedAmount = bigMath.mulDiv(token.managedUsd, expandDecimals(1, token.decimals), token.minPrice);

      setTokenUsingIndexPrices(token, indexPrices, nativeTokenAddress);
    }

    if (fundingRateInfo) {
      token.fundingRate = fundingRateInfo[i * fundingRatePropsLength];
      token.cumulativeFundingRate = fundingRateInfo[i * fundingRatePropsLength + 1];
    }

    const lowerAddr = token.address.toLowerCase();
    if (infoTokens[lowerAddr]) {
      token.balance = infoTokens[lowerAddr].balance;
    }

    infoTokens[lowerAddr] = token;
  }

  // Apply SynthVault OI data (read directly from storage, no price feed involved)
  if (synthOiData && synthTokens.length > 0) {
    for (let i = 0; i < synthTokens.length; i++) {
      const oi = synthOiData[i];
      if (!oi) continue;

      const lowerAddr = synthTokens[i].address.toLowerCase();
      const token = infoTokens[lowerAddr];
      if (!token) continue;

      const { poolAmount, reservedAmount, guaranteedUsd, globalShortSize, globalLongSize, maxGlobalShortSize, maxGlobalLongSize } = oi;

      token.poolAmount        = poolAmount;
      token.reservedAmount    = reservedAmount;
      token.availableAmount   = poolAmount > reservedAmount ? poolAmount - reservedAmount : 0n;
      token.guaranteedUsd     = guaranteedUsd;
      token.globalShortSize   = globalShortSize;
      // Synthetic long OI (keyed by index token) — the display + capacity source
      // for longs, since guaranteedUsd is 0 for synthetic longs (keyed by BUSDC).
      token.globalLongSize    = globalLongSize;
      token.maxGlobalShortSize = maxGlobalShortSize;
      token.maxGlobalLongSize  = maxGlobalLongSize;

      // Apply funding rates from SynthVault (same shape as Reader.getFundingRates).
      if (synthFundingRates) {
        token.fundingRate = synthFundingRates[i * 2];
        token.cumulativeFundingRate = synthFundingRates[i * 2 + 1];
      }

      // Synthetic markets draw from the shared stable (BUSDC) pool — the index
      // token is never held (poolAmount = 0), so the pool-based availableUsd
      // must come from the stable pool, not this token's (zero) balance.
      token.availableUsd = synthStablePoolUsd;

      token.maxAvailableShort = 0n;
      token.hasMaxAvailableShort = false;
      if (maxGlobalShortSize > 0n) {
        token.hasMaxAvailableShort = true;
        if (maxGlobalShortSize > globalShortSize) {
          const remainingShort = maxGlobalShortSize - globalShortSize;
          // shorts are also bounded by what the stable pool can back
          token.maxAvailableShort = remainingShort < synthStablePoolUsd ? remainingShort : synthStablePoolUsd;
        }
      }

      token.maxAvailableLong = 0n;
      token.hasMaxAvailableLong = false;
      if (maxGlobalLongSize > 0n) {
        token.hasMaxAvailableLong = true;
        if (maxGlobalLongSize > globalLongSize) {
          const remaining = maxGlobalLongSize - globalLongSize;
          token.maxAvailableLong = remaining < token.availableUsd ? remaining : token.availableUsd;
        }
      } else {
        token.maxAvailableLong = token.availableUsd;
      }

      token.maxLongCapacity =
        maxGlobalLongSize > 0n && maxGlobalLongSize < token.availableUsd + globalLongSize
          ? maxGlobalLongSize
          : token.availableUsd + globalLongSize;

      token.managedUsd = token.availableUsd + guaranteedUsd;
    }
  }

  return infoTokens;
}

function setTokenUsingIndexPrices(
  token: TokenInfo,
  indexPrices: { [address: string]: bigint },
  nativeTokenAddress: string
) {
  if (!indexPrices) {
    return;
  }

  const tokenAddress = (token.isNative ? nativeTokenAddress : token.address).toLowerCase();
  const indexPrice = indexPrices[tokenAddress];

  if (indexPrice === undefined) {
    return;
  }

  const indexPriceBn = BigInt(indexPrice);
  if (indexPriceBn == 0n) {
    return;
  }

  // No vault price base — synthetic token not whitelisted in Vault. Use live price directly.
  if (!token.minPrice || !token.maxPrice) {
    token.minPrice = indexPriceBn;
    token.maxPrice = indexPriceBn;
    token.contractMinPrice = indexPriceBn;
    token.contractMaxPrice = indexPriceBn;
    return;
  }

  const spread = token.maxPrice! - token.minPrice!;
  const spreadBps = bigMath.mulDiv(spread, BASIS_POINTS_DIVISOR_BIGINT, (token.maxPrice! + token.minPrice!) / 2n);

  if (spreadBps > MAX_PRICE_DEVIATION_BASIS_POINTS - 50) {
    if (indexPriceBn > token.minPrimaryPrice!) {
      token.maxPrice = indexPriceBn;
    } else {
      token.minPrice = indexPriceBn;
    }
    return;
  }

  const halfSpreadBps = spreadBps / 2n;
  token.maxPrice = bigMath.mulDiv(indexPriceBn, BASIS_POINTS_DIVISOR_BIGINT + halfSpreadBps, BASIS_POINTS_DIVISOR_BIGINT);
  token.minPrice = bigMath.mulDiv(indexPriceBn, BASIS_POINTS_DIVISOR_BIGINT - halfSpreadBps, BASIS_POINTS_DIVISOR_BIGINT);
}
